import React, { useState, useRef, useEffect } from 'react';
import { Check, Loader2, Wand2, FileSpreadsheet, Eye, EyeOff, X, Camera } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import ImageDropzone from './ImageDropzone';
import WebcamCapture, { WebcamCaptureRef } from './WebcamCapture';
import { v4 as uuidv4 } from 'uuid';

interface Step {
  title: string;
  description: string;
  subtexts: string[];
  singleImageSubtexts?: string[];
  interval: number;
}

interface ProcessingPopupProps {
  isOpen: boolean;
  currentStep: number;
  streetViewImage?: string | null;
  zillowImages?: string[];
  webImages?: Array<{
    webUrl: string;
    imageUrl: string;
    uploadedUrl?: string;
    file?: Buffer | string;
  }>;
  onGenerateAI?: () => void;
  isGeneratingAI?: boolean;
  onAssessSuitability?: () => void;
  isAssessing?: boolean;
  selectedImage: string | null;
  selectionReason?: string | null;
  onCreateSheet?: () => void;
  isCreatingSheet?: boolean;
  showImages?: boolean;
  onToggleShowImages?: () => void;
  onUploadImage?: (file?: File) => void;
  allImagesCount?: number;
  // Add new props for single image toggle
  singleImageMode?: boolean;
  onToggleSingleImageMode?: (enabled: boolean) => void;
  onClose?: () => void;
}

const STEPS: Step[] = [
  {
    title: 'Looking for images',
    description: 'Searching for your property...',
    subtexts: [
      'Checking google streetview',
      'Looking for property pictures', 
      'Looking at additional web images'
    ],
    interval: 3000 // 3 seconds
  },
  {
    title: 'Assessing suitability',
    description: 'Analyzing image quality...',
    subtexts: [
      'Identifying exterior images',
      'Analyzing image quality',
      'Selecting best candidate'
    ],
    interval: 3000 // 3 seconds
  },
  {
    title: 'Generating AI art',
    description: 'Creating your illustrations...',
    subtexts: [
      'Creating AI illustration template',
      'Gathering my brushes',
      'Mixing my oil paints',
      'Sharpening my pencils',
      'Creating watercolor illustration',
      'Creating oil painting',
      'Creating pencil drawing'
    ],
    singleImageSubtexts: [
      'Creating AI illustration template',
      'Gathering my brushes',
      'Creating watercolor illustration',
      'Finalizing your artwork'
    ],
    interval: 3000 // 3 seconds
  }
];

const ProcessingPopup: React.FC<ProcessingPopupProps> = ({ 
  isOpen, 
  currentStep, 
  streetViewImage,
  zillowImages = [],
  webImages = [],
  onGenerateAI,
  isGeneratingAI = false,
  onAssessSuitability,
  isAssessing = false,
  selectedImage,
  selectionReason,
  onCreateSheet,
  isCreatingSheet = false,
  showImages = false,
  onToggleShowImages,
  onUploadImage,
  allImagesCount = 0,
  // Add new props for single image toggle
  singleImageMode = false,
  onToggleSingleImageMode,
  onClose
}) => {
  const navigate = useNavigate();
  const [showWebcam, setShowWebcam] = useState(false);
  const webcamRef = useRef<WebcamCaptureRef>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [stepPercentages, setStepPercentages] = useState<number[]>([0, 0, 0]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [currentSubtextIndex, setCurrentSubtextIndex] = useState(0);
  const subtextIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Check if confidence level is low
  const hasLowConfidence = selectionReason?.includes('Confidence Level: LOW') || false;

  // Reset subtext index when step changes
  useEffect(() => {
    setCurrentSubtextIndex(0);
  }, [currentStep]);

  // Handle subtext cycling
  useEffect(() => {
    if (!isOpen || showImages) return;

    // Clear any existing interval
    if (subtextIntervalRef.current) {
      clearInterval(subtextIntervalRef.current);
    }

    // Only cycle subtexts for active steps
    const step = STEPS[currentStep];
    if (step && step.subtexts && step.subtexts.length > 1) {
      subtextIntervalRef.current = setInterval(() => {
        setCurrentSubtextIndex(prevIndex => {
          // Special handling for step 3 (Generating AI art)
          if (currentStep === 2) {
            // Use appropriate subtexts based on single image mode
            const subtexts = (singleImageMode && step.singleImageSubtexts) 
              ? step.singleImageSubtexts 
              : step.subtexts;
            
            if (singleImageMode && step.singleImageSubtexts) {
              // For single image mode, cycle through all messages normally
              return (prevIndex + 1) % subtexts.length;
            } else {
              // Original logic for multi-image mode: Messages: 0,1,2,3,4,5,6 then repeat 4,5,6
              if (prevIndex < 6) {
                return prevIndex + 1;
              } else {
                // After message 6, go back to message 4
                return 4;
              }
            }
          } else {
            // Normal cycling for other steps
            return (prevIndex + 1) % step.subtexts.length;
          }
        });
      }, step.interval);
    }

    // Cleanup function
    return () => {
      if (subtextIntervalRef.current) {
        clearInterval(subtextIntervalRef.current);
      }
    };
  }, [isOpen, currentStep, showImages, singleImageMode]);

  // Cleanup interval when component unmounts or popup closes
  useEffect(() => {
    if (!isOpen) {
      // Reset webcam state when popup closes
      setShowWebcam(false);
      // Clear subtext interval
      if (subtextIntervalRef.current) {
        clearInterval(subtextIntervalRef.current);
      }
      setCurrentSubtextIndex(0);
      // Reset step percentages when popup closes
      setStepPercentages([0, 0, 0]);
    }
  }, [isOpen]);

  // Step-specific percentage counters
  useEffect(() => {
    if (!isOpen) return;

    // Step durations: Step 1: 20s, Step 2: 20s, Step 3: 100s
    const stepDurations = [20000, 20000, 100000]; // in milliseconds
    const interval = 100; // Update every 100ms for smooth animation

    const timers: NodeJS.Timeout[] = [];

    // Create timer for current step
    if (currentStep >= 0 && currentStep < stepDurations.length) {
      const duration = stepDurations[currentStep];
      const increment = (100 / duration) * interval;

      // Reset current step percentage when step starts, but keep previous steps at 100%
      setStepPercentages(prev => {
        const newPercentages = [...prev];
        // Set previous steps to 100% if they haven't been set yet
        for (let i = 0; i < currentStep; i++) {
          if (newPercentages[i] < 100) {
            newPercentages[i] = 100;
          }
        }
        // Reset current step to 0
        newPercentages[currentStep] = 0;
        return newPercentages;
      });

      const timer = setInterval(() => {
        setStepPercentages(prev => {
          const newPercentages = [...prev];
          const newPercentage = newPercentages[currentStep] + increment;
          if (newPercentage >= 100) {
            clearInterval(timer);
            newPercentages[currentStep] = 100;
          } else {
            newPercentages[currentStep] = newPercentage;
          }
          return newPercentages;
        });
      }, interval);

      timers.push(timer);
    }

    // Cleanup timers when step changes or popup closes
    return () => {
      timers.forEach(timer => clearInterval(timer));
    };
  }, [isOpen, currentStep]);

  const handleImageDrop = async (file: File) => {
    if (!onUploadImage) return;
    
    try {
      setIsUploading(true);
      setUploadError(null);
      onUploadImage(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle webcam capture
  const handleImageCapture = async (imageData: string) => {
    if (!onUploadImage) return;
    
    try {
      setIsUploading(true);
      setUploadError(null);
      
      // Convert base64 to blob
      const base64Response = await fetch(imageData);
      const blob = await base64Response.blob();
      
      // Create a file from the blob
      const filename = `webcam-${uuidv4()}.jpg`;
      const file = new File([blob], filename, { type: 'image/jpeg' });
      
      // Pass to parent component
      onUploadImage(file);
      
      // Close webcam view after successful upload
      setShowWebcam(false);
    } catch (error) {
      console.error('Error processing webcam image:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to process image');
    } finally {
      setIsUploading(false);
    }
  };
  
  if (!isOpen) return null;
  
  const allImages = [
    ...(streetViewImage ? [streetViewImage] : []),
    ...zillowImages,
    ...webImages.map(w => w.uploadedUrl || w.imageUrl)
  ].filter(Boolean);

  const uploadBuffer = async (base64Data: string, filename: string) => {
    try {
      // Convert base64 to blob
      const base64 = base64Data.replace(/^data:image\/\w+;base64,/, '');
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });
      
      // Upload to Supabase storage
      const { data, error } = await supabase.storage
        .from('propertyimages')
        .upload(filename, blob, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('propertyimages')
        .getPublicUrl(filename);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };

  // Helper function to check if an image is selected
  const isImageSelected = (imageUrl: string) => {
    return selectedImage === imageUrl;
  };

  const handleCreateContactSheet = () => {
    if (onCreateSheet) {
      onCreateSheet();
    }
  };

  // Get current subtext for the current step
  const getCurrentSubtext = () => {
    const step = STEPS[currentStep];
    if (step && step.subtexts && step.subtexts.length > 0) {
      // Use single image subtexts for step 2 (AI generation) when in single image mode
      const subtexts = (currentStep === 2 && singleImageMode && step.singleImageSubtexts) 
        ? step.singleImageSubtexts 
        : step.subtexts;
      
      return subtexts[currentSubtextIndex] || subtexts[0];
    }
    return step?.description || '';
  };

  // Get completion message for completed steps
  const getCompletionMessage = (stepIndex: number) => {
    if (stepIndex === 0) {
      // Looking for images - show count
      return `Found ${allImagesCount} images related to the address`;
    } else if (stepIndex === 1) {
      // Assessing suitability - show identified
      return 'Selected most appropriate image';
    }
    return STEPS[stepIndex]?.description || '';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4">
      <div className="bg-[#F5ECE1] w-[320px] rounded-2xl shadow-xl">
        <div className="relative p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Framing your home
            </h2>
            <button
              onClick={onToggleShowImages}
              className="text-sm font-medium opacity-0 hover:opacity-0 flex items-center gap-1"
            >
              {showImages ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          <div className="w-full h-px bg-gray-400 mb-6" />
          {showImages ? (
            <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Street View</h3>
                {streetViewImage ? (
                  <div className="relative aspect-square w-full h-32">
                    <img
                      src={streetViewImage}
                      alt="Street View"
                      className={`w-full h-full object-cover rounded-lg shadow-sm transition-all transform ${
                        isImageSelected(streetViewImage) ? 'ring-[6px] ring-purple-500 ring-opacity-80 scale-105 shadow-xl' : ''
                      }`}
                    />
                    <div className="absolute top-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                      1
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-400">No street view available</span>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Zillow Images</h3>
                <div className="grid grid-cols-3 gap-2">
                  {zillowImages.length > 0 ? (
                    zillowImages.map((url, index) => {
                      // Calculate image number - street view is 1, so zillow starts at 2
                      const imageNumber = (streetViewImage ? 2 : 1) + index;
                      
                      return (
                        <div key={index} className="relative aspect-square">
                          <img
                            src={url}
                            alt={`Zillow ${index + 1}`}
                            className={`absolute inset-0 w-full h-full object-cover rounded-lg shadow-sm hover:opacity-90 transition-all cursor-pointer ${
                              isImageSelected(url) ? 'ring-[6px] ring-purple-500 ring-opacity-80 scale-105 shadow-xl' : ''
                            }`}
                            onClick={() => window.open(url, '_blank')}
                          />
                          <div className="absolute top-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                            {imageNumber}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    Array(3).fill(null).map((_, index) => (
                      <div key={index} className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-xs text-gray-400">No image</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Additional Images</h3>
                <div className="grid grid-cols-3 gap-2">
                  {webImages.length > 0 ? (
                    webImages.map((url, index) => {
                      // Calculate image number - continue from zillow images
                      const imageNumber = (streetViewImage ? 2 : 1) + zillowImages.length + index;
                      
                      return (
                        <div
                          key={index}
                          className="relative aspect-square"
                          onClick={() => window.open(url.webUrl, '_blank')}
                        >
                          <img
                            src={url.uploadedUrl || url.imageUrl}
                            alt={`Web image ${index + 1}`}
                            className={`absolute inset-0 w-full h-full object-cover rounded-lg shadow-sm hover:opacity-90 transition-all cursor-pointer bg-white ${
                              isImageSelected(url.uploadedUrl || url.imageUrl) ? 'ring-[6px] ring-purple-500 ring-opacity-80 scale-105 shadow-xl' : ''
                            }`}
                          />
                          <div className="absolute top-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                            {imageNumber}
                          </div>
                          {url.file && !url.uploadedUrl && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (typeof url.file === 'string' && url.file !== '[Buffer]') {
                                  const uploadedUrl = await uploadBuffer(url.file, `web-image-${Date.now()}.jpg`);
                                  if (uploadedUrl) {
                                    url.uploadedUrl = uploadedUrl;
                                    // Force re-render by creating a new array
                                    webImages.splice(0, webImages.length, ...webImages);
                                  }
                                }
                              }}
                              className="absolute bottom-1 left-1 text-[10px] text-blue-500 hover:text-blue-700 bg-white/90 px-1.5 py-0.5 rounded"
                            >
                              Upload
                            </button>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    Array(3).fill(null).map((_, index) => (
                      <div key={index} className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-xs text-gray-400">No image</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              <div className="text-xs text-gray-500 mt-4">
                {allImages.length > 0 ? "Click any image to view full size" : "No images found yet"}
              </div>
              
              {/* Single Image Toggle */}
              {onToggleSingleImageMode && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Generate Single Image</h4>
                      <p className="text-xs text-gray-500">Generate only watercolor style (faster)</p>
                    </div>
                    <button
                      onClick={() => onToggleSingleImageMode(!singleImageMode)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        singleImageMode ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          singleImageMode ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}
              
              <div className="mt-4 space-y-3">
                {onCreateSheet && allImages.length > 0 && (
                  <button
                    onClick={handleCreateContactSheet}
                    disabled={isCreatingSheet}
                    className={`w-full flex items-center justify-center gap-2 bg-green-600 text-white py-2.5 px-4 rounded-lg transition-colors ${
                      isCreatingSheet ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'
                    }`}
                  >
                    {isCreatingSheet ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Creating Sheet...</span>
                      </>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-4 h-4" />
                        <span>Create Contact Sheet</span>
                      </>
                    )}
                  </button>
                )}
                
                {onAssessSuitability && allImages.length > 0 && !selectedImage && (
                  <button
                    onClick={onAssessSuitability}
                    disabled={isAssessing || allImages.length === 0}
                    className={`w-full flex items-center justify-center gap-2 bg-black text-white py-2.5 px-4 rounded-lg transition-colors ${
                      isAssessing || allImages.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
                    }`}
                  >
                    {isAssessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Assessing Images...</span>
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        <span>Assess Suitability</span>
                      </>
                    )}
                  </button>
                )}
                
                {selectedImage && hasLowConfidence && (
                  <div className="p-4 rounded-lg border border-gray-200 bg-white mb-4 text-left">
                    <p className="text-sm text-gray-900">
                      We couldn't find a suitable image
                    </p>
                  </div>
                )}
                
                {onGenerateAI && (
                  <button
                    onClick={onGenerateAI}
                    disabled={isGeneratingAI || isAssessing || !selectedImage || hasLowConfidence}
                    className={`w-full flex items-center justify-center gap-2 bg-purple-600 text-white py-2.5 px-4 rounded-full transition-colors ${
                      isGeneratingAI || isAssessing || !selectedImage || hasLowConfidence ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-700'
                    }`}
                  >
                    {isGeneratingAI ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Generating AI Images...</span>
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        <span>Generate AI Images</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div>
              {/* Replace steps with upload interface when confidence is low and we're at step 2 */}
              {currentStep >= 1 && hasLowConfidence ? (
                <div className="space-y-6">
                  {showWebcam ? (
                    <div className="text-left mb-4">
                      <h3 className="text-base font-medium text-gray-900">
                        Take a picture of your home
                      </h3>
                    </div>
                  ) : (
                    <div className="text-left mb-6">
                      <h3 className="text-base font-medium text-gray-900">
                        We couldn't find a suitable image
                      </h3>
                      <p className="text-sm text-gray-600 mt-2">
                        Please upload your own image for the best results.
                      </p>
                    </div>
                  )}
                  
                  {uploadError && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                      {uploadError}
                    </div>
                  )}
                  
                  {showWebcam ? (
                    <>
                      <WebcamCapture 
                        ref={webcamRef}
                        onCapture={handleImageCapture} 
                        onBack={() => setShowWebcam(false)}
                      />
                    </>
                  ) : (
                    <ImageDropzone 
                      onImageDrop={handleImageDrop} 
                    />
                  )}
                </div>
              ) : (
                // Show normal steps when confidence is not low
                STEPS.map((step, index) => {
                  const isActive = index === currentStep;
                  const isComplete = index < currentStep;
                  
                  return (
                    <div 
                      key={step.title}
                      className={`relative pl-24 ${index !== STEPS.length - 1 ? 'pb-10' : ''}`}
                    >
                      {/* Vertical Line */}
                      {index !== STEPS.length - 1 && (
                        <div 
                          className={`absolute left-[19px] top-[40px] w-[2px] h-[calc(100%-20px)] transition-all duration-300 ${
                            index < currentStep ? 'bg-gray-400' : 'bg-gray-300'
                          }`}
                        />
                      )}

                      {/* Circle with Check/Number and Spinning Ring */}
                      <div 
                        className={`absolute left-0 top-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                          isComplete ? (index === 1 && hasLowConfidence ? 'border-red-500 bg-red-500 text-white' : 'border-black bg-black text-white') :
                          isActive ? 'border-black text-black' :
                          'border-gray-200 text-gray-400'
                        }`}
                      >
                        {/* Spinning Ring */}
                        {isActive && (
                          <div className="absolute inset-0 rounded-full border-2 border-black border-t-transparent animate-spin" />
                        )}
                        {/* Inner Circle */}
                        <div className={`absolute inset-[2px] rounded-full ${
                          isComplete ? (index === 1 && hasLowConfidence ? 'bg-red-500' : 'bg-black') :
                          'bg-white border-2 border-current'
                        } flex items-center justify-center`}>
                        {isComplete ? (
                          index === 1 && hasLowConfidence ? (
                            <X className="w-4 h-4 stroke-[3]" />
                          ) : (
                            <Check className="w-4 h-4 stroke-[3]" />
                          )
                        ) : isActive ? (
                          <span className="text-xs font-normal text-gray-600">
                            {Math.round(stepPercentages[index])}%
                          </span>
                        ) : (
                          <span className="text-sm font-medium">{index + 1}</span>
                        )}
                        </div>
                      </div>

                      {/* Content - Always 2 lines with consistent height */}
                      <div className={`transition-all duration-300 ${
                        isActive ? 'opacity-100' :
                        isComplete ? 'opacity-50' :
                        'opacity-40'
                      }`}>
                        <div className="h-12 flex flex-col justify-between">
                          <h3 className="text-base font-semibold text-gray-900 leading-5 h-5">
                            {step.title}
                          </h3>
                          <p className={`text-sm leading-5 h-5 ${
                            isActive ? 'text-gray-600' : 
                            isComplete ? 'text-gray-500' :
                            'text-gray-500'
                          }`}>
                            {isActive ? getCurrentSubtext() : 
                             isComplete ? getCompletionMessage(index) :
                             step.description}
                          </p>
                        </div>
                        
                        {/* Show message for low confidence at step 2 */}
                        {index === 1 && isComplete && hasLowConfidence && (
                          <div className="mt-2 text-sm text-gray-700">
                            We couldn't find a suitable image online
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}

              {/* Webcam trigger centered below everything */}
              {currentStep >= 1 && hasLowConfidence && !showImages && !showWebcam && (
                <div className="mt-6">
                  <button 
                    className="w-full flex items-center justify-center gap-2 bg-black text-white py-2 px-4 rounded-full hover:bg-gray-800 transition-colors"
                    onClick={() => setShowWebcam(true)}
                  >
                    <Camera className="w-4 h-4" />
                    <span>Take a Picture</span>
                  </button>
                </div>
              )}

              {/* Close button under Take a Picture */}
              {currentStep >= 1 && hasLowConfidence && !showImages && !showWebcam && onClose && (
                <div className="mt-[20px] flex justify-center">
                  <button 
                    className="flex items-center justify-center gap-2 text-black hover:opacity-70 transition-opacity"
                    onClick={onClose}
                  >
                    <X className="w-4 h-4" />
                    <span>Close</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessingPopup;